import { MenuItem } from '@affine/component';
import { ServerService, UserFeatureService } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import {
  AccountIcon,
  AdminIcon,
  MapPanelIcon,
  PhonePanelIcon,
  SignOutIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect } from 'react';

import { WorkbenchService } from '../../../modules/workbench';
import { useSignOut } from '../../hooks/affine/use-sign-out';

export const AccountMenu = () => {
  const workspaceDialogService = useService(WorkspaceDialogService);
  const openSignOutModal = useSignOut();
  const serverService = useService(ServerService);
  const userFeatureService = useService(UserFeatureService);
  const workbench = useService(WorkbenchService).workbench;
  const isAFFiNEAdmin = useLiveData(userFeatureService.userFeature.isAdmin$);

  // pipe: the employee's OWN location-sharing screen (/me/location) lives in the profile menu —
  // it is personal settings, not a business area, so it gets no sidebar area entry. Navigation goes
  // through `workbench.open` (the ERP navigation SSOT) so the URL bar and browser back stay
  // truthful. Ungated by design: it is the consent + "what has been reported about me" screen,
  // which every employee is entitled to see; sharing itself stays double opt-in and server-gated.
  // The person's OWN profile (work phone + time zone) — personal settings, ungated for the same
  // reason; the server only ever reads or writes the signed-in person's own staff record.
  const onOpenMyProfile = useCallback(
    () => workbench.open('/me/profile'),
    [workbench]
  );
  const onOpenMyLocation = useCallback(
    () => workbench.open('/me/location'),
    [workbench]
  );

  const onOpenAccountSetting = useCallback(() => {
    track.$.navigationPanel.profileAndBadge.openSettings({ to: 'account' });
    workspaceDialogService.open('setting', {
      activeTab: 'account',
    });
  }, [workspaceDialogService]);

  const onOpenAdminPanel = useCallback(() => {
    window.open(`${serverService.server.baseUrl}/admin`, '_blank');
  }, [serverService.server.baseUrl]);

  const t = useI18n();

  useEffect(() => {
    userFeatureService.userFeature.revalidate();
  }, [userFeatureService]);

  return (
    <>
      <MenuItem
        prefixIcon={<AccountIcon />}
        data-testid="workspace-modal-account-settings-option"
        onClick={onOpenAccountSetting}
      >
        {t['com.affine.workspace.cloud.account.settings']()}
      </MenuItem>
      <MenuItem
        prefixIcon={<PhonePanelIcon />}
        data-testid="pipe-my-profile-option"
        onClick={onOpenMyProfile}
      >
        {t['com.affine.pipe.me.profile.nav']()}
      </MenuItem>
      <MenuItem
        prefixIcon={<MapPanelIcon />}
        data-testid="pipe-my-location-option"
        onClick={onOpenMyLocation}
      >
        {t['com.affine.pipe.me.location.nav']()}
      </MenuItem>
      {isAFFiNEAdmin ? (
        <MenuItem
          prefixIcon={<AdminIcon />}
          data-testid="workspace-modal-account-admin-option"
          onClick={onOpenAdminPanel}
        >
          {t['com.affine.workspace.cloud.account.admin']()}
        </MenuItem>
      ) : null}
      <MenuItem
        prefixIcon={<SignOutIcon />}
        data-testid="workspace-modal-sign-out-option"
        onClick={openSignOutModal}
      >
        {t['com.affine.workspace.cloud.account.logout']()}
      </MenuItem>
    </>
  );
};
